import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { BoDialogComponent } from '@3ddv/software-division-components/backoffice/dialog';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { BrnDialogRef } from '@spartan-ng/brain/dialog';
import { ToastrService } from 'ngx-toastr';
import { finalize, Subscription } from 'rxjs';
import { DialogCloseReason } from 'src/app/private/layout/dialog/app-dialog.service';
import { UtilsService } from 'src/app/private/services/utils.service';
import { User, USER_TYPES } from '../../models/users.model';
import { UsersService } from '../../services/users.service';

export type ModalAction = 'edit' | 'delete' | 'create';

@Component({
  selector: 'app-upload-result',
  templateUrl: './upload-result.component.html',
  styleUrls: ['./upload-result.component.css'],
  imports: [BoDialogComponent, ButtonComponent],
})
export class UploadResultComponent implements OnInit, OnDestroy {
  private dialogRef = inject<BrnDialogRef<DialogCloseReason>>(BrnDialogRef);
  private usersService = inject(UsersService);
  private utils = inject(UtilsService);
  private toastr = inject(ToastrService);

  @Input() data: Array<User> = [];

  public validationResult: Array<Record<string, boolean>>;
  private subscriptions: Array<Subscription>;
  public isUploaded = false;
  public refreshing = false;
  public hasErrors = false;

  constructor() {
    this.subscriptions = [];
  }

  ngOnInit(): void {
    this.validationResult = this.validateData(this.data);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  public onDismiss(reason: DialogCloseReason) {
    this.dialogRef.close(reason);
  }

  public onConfirm() {
    if (this.isUploaded) {
      this.onDismiss('success');
      return;
    }

    this.hasErrors = false;
    this.refreshing = true;

    const uploadSub = this.usersService
      .uploadCsv$(this.data)
      .pipe(finalize(() => (this.refreshing = false)))
      .subscribe(
        res => {
          this.isUploaded = true;
          this.hasErrors = this.data.length !== res.data.length;

          const savedUsersMap = new Map(
            res.data.map(user => [user.username, user])
          );

          this.data = this.data.map(user => {
            const savedUser = savedUsersMap.get(user.username);

            if (savedUser) {
              return { ...user, ...savedUser };
            }

            return user;
          });
        },
        error => {
          this.toastr.error(error?.error?.message, `Error saving users`);
          console.error(`Error saving users`, error);
        }
      );

    this.subscriptions.push(uploadSub);
  }

  private validateData(data: Array<User>) {
    return data.map(user => {
      const username = this.utils.isEmpty(user.username);
      const password = !this.utils.isValidPassword(user.password);
      const email = !this.utils.isValidEmail(user.email);
      const type = Object.values(USER_TYPES).every(v => v !== user.type);

      if (!this.hasErrors) {
        this.hasErrors = username || password || email || type;
      }

      return {
        username,
        password,
        email,
        type,
      };
    });
  }
}
